import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    BuildingOfficeIcon,
    EyeIcon,
    PhoneIcon,
    EnvelopeIcon,
    MapPinIcon,
    ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';

// API_BASE already points to "/api"
const API = API_BASE;

const AdminEmployersPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [employers, setEmployers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEmployer, setSelectedEmployer] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        if (!user) {
            toast.error('ログインが必要です');
            navigate('/login');
            return;
        }
        // Only Admin can access
        if (user.user_type !== 'admin') {
            toast.error('このページは管理者専用です');
            navigate('/');
            return;
        }

        fetchEmployers();
    }, [user, navigate]);

    const fetchEmployers = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API}/admin/users?type=employer`);

            const adapted = res.data.map(u => ({
                ...u,
                phone: u.phone || '-',
                location: u.location || '-',
                description: u.description || '-',
                rating: u.rating || 0,
                completed_jobs: u.completed_jobs_count || 0,
                last_active: u.last_active_at
            }));

            setEmployers(adapted);
        } catch (error) {
            console.error(error);
            toast.error('事業者データの取得に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const filteredEmployers = employers.filter(emp =>
        emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.location.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const viewEmployerDetails = (emp) => {
        setSelectedEmployer(emp);
        setShowModal(true);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">事業者管理</h1>
                    <p className="text-gray-600">
                        登録済み事業者の情報管理と活動状況の監視
                    </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <BuildingOfficeIcon className="w-6 h-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">総事業者数</p>
                                <p className="text-2xl font-bold text-gray-900">{employers.length}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <ShieldCheckIcon className="w-6 h-6 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">本人確認済み</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {employers.filter(e => e.is_verified).length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm">
                        <div className="flex items-center">
                            <div className="p-2 bg-orange-100 rounded-lg">
                                <BuildingOfficeIcon className="w-6 h-6 text-orange-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">今月新規登録</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {employers.filter(e => {
                                        const created = new Date(e.created_at);
                                        const now = new Date();
                                        return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
                                    }).length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                    <div className="flex items-center space-x-4">
                        <div className="flex-1">
                            <input
                                type="text"
                                placeholder="会社名、メール、地域で検索..."
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="text-sm text-gray-600">
                            {filteredEmployers.length}社の事業者
                        </div>
                    </div>
                </div>

                {/* List */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">事業者一覧</h2>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        事業者情報
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        連絡先
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ステータス
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        最終ログイン
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        操作
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredEmployers.map((emp) => (
                                    <tr key={emp.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">{emp.name}</div>
                                                <div className="text-sm text-gray-500">{emp.location}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="space-y-1">
                                                <div className="flex items-center">
                                                    <EnvelopeIcon className="w-4 h-4 mr-2" />
                                                    {emp.email}
                                                </div>
                                                <div className="flex items-center">
                                                    <PhoneIcon className="w-4 h-4 mr-2" />
                                                    {emp.phone}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${emp.is_verified ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                {emp.is_verified ? '本人確認済' : '未確認'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(emp.last_active)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button
                                                onClick={() => viewEmployerDetails(emp)}
                                                className="text-blue-600 hover:text-blue-700 flex items-center mr-3"
                                            >
                                                <EyeIcon className="w-4 h-4 mr-1" />
                                                詳細
                                            </button>
                                            <button
                                                onClick={async () => {
                                                    if (!window.confirm(`このユーザーを${emp.status === 'suspended' ? '有効化' : '停止'}しますか？`)) return;
                                                    try {
                                                        const newStatus = emp.status === 'suspended' ? 'active' : 'suspended';
                                                        await axios.put(`${API}/admin/users/${emp.id}/status`, { status: newStatus });
                                                        toast.success(`ユーザーステータスを更新しました`);
                                                        setEmployers(prev => prev.map(u => u.id === emp.id ? { ...u, status: newStatus } : u));
                                                    } catch (e) {
                                                        toast.error('更新に失敗しました');
                                                    }
                                                }}
                                                className={`flex items-center ${emp.status === 'suspended' ? 'text-green-600 hover:text-green-700' : 'text-red-600 hover:text-red-700'}`}
                                            >
                                                <ShieldCheckIcon className="w-4 h-4 mr-1" />
                                                {emp.status === 'suspended' ? '有効化' : '停止'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Modal */}
                {showModal && selectedEmployer && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">事業者詳細情報</h3>
                                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">×</button>
                            </div>

                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">会社名/氏名</label>
                                        <p className="mt-1 text-sm text-gray-900">{selectedEmployer.name}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">登録日</label>
                                        <p className="mt-1 text-sm text-gray-900">{formatDate(selectedEmployer.created_at)}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">メールアドレス</label>
                                        <p className="mt-1 text-sm text-gray-900">{selectedEmployer.email}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">電話番号</label>
                                        <p className="mt-1 text-sm text-gray-900">{selectedEmployer.phone}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">所在地</label>
                                        <p className="mt-1 text-sm text-gray-900">{selectedEmployer.location}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">最終ログイン</label>
                                        <p className="mt-1 text-sm text-gray-900">{formatDate(selectedEmployer.last_active)}</p>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">自己紹介/会社概要</label>
                                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedEmployer.description || 'なし'}</p>
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                                >
                                    閉じる
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminEmployersPage;
